import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, forkJoin, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { environment } from '../../../../environments/environment';
import { TaskModel } from '../../models/task.model';
import { AccountModel } from '../../models/account.model';
import { TaskStatusEnum } from '../../enums/task-status.enum';

export interface DashboardStats {
  openTasks: number;
  activeAccounts: number;
  totalProperties: number;
  pendingEnrollments: number;
}

export interface RecentTask {
  id: number;
  type: string;
  dateReceived: Date;
  status: string;
}

export interface ActivityItem {
  id: number;
  icon: string;
  iconColor: string;
  description: string;
  timestamp: Date;
}

const FALLBACK_STATS: DashboardStats = {
  openTasks: 0,
  activeAccounts: 0,
  totalProperties: 0,
  pendingEnrollments: 0,
};

@Injectable({
  providedIn: 'root'
})
export class DashboardService {
  private readonly taskUrl = `${environment.apiUrl}/task`;
  private readonly accountUrl = `${environment.apiUrl}/account`;

  constructor(private readonly http: HttpClient) {}

  getDashboardStats(): Observable<DashboardStats> {
    return this.http.get<DashboardStats>(`${environment.apiUrl}/api/dashboard/stats`).pipe(
      catchError(() => of(FALLBACK_STATS))
    );
  }

  getRecentTasks(): Observable<RecentTask[]> {
    return this.http.get<TaskModel[]>(this.taskUrl).pipe(
      map(tasks =>
        tasks.slice(0, 5).map(t => ({
          id: t.id,
          type: t.type,
          dateReceived: t.dateReceived,
          status: t.status,
        }))
      ),
      catchError(() => of([] as RecentTask[]))
    );
  }

  getRecentActivity(): Observable<ActivityItem[]> {
    return this.http.get<ActivityItem[]>(`${environment.apiUrl}/api/dashboard/activity`).pipe(
      catchError(() => of([
      {
        id: 1,
        icon: 'check_circle',
        iconColor: '#16a34a',
        description: 'Task #1040 approved — New Customer enrollment for Sunrise Rentals LLC',
        timestamp: new Date('2026-04-01T09:30:00')
      },
      {
        id: 2,
        icon: 'person_add',
        iconColor: '#2563eb',
        description: 'New account registered: Ocean View Properties (Acct #8821)',
        timestamp: new Date('2026-04-01T08:45:00')
      },
      {
        id: 3,
        icon: 'home_work',
        iconColor: '#7c3aed',
        description: 'Property added: 412 Beachside Blvd, Unit 3A linked to Acct #8801',
        timestamp: new Date('2026-03-31T15:20:00')
      },
      {
        id: 4,
        icon: 'cancel',
        iconColor: '#dc2626',
        description: 'Task #1039 declined — Remove Property request for 88 Harbor Ln',
        timestamp: new Date('2026-03-31T11:10:00')
      },
      {
        id: 5,
        icon: 'assignment_turned_in',
        iconColor: '#16a34a',
        description: 'Task #1038 approved — New Property at 217 Gulf Shores Dr',
        timestamp: new Date('2026-03-30T17:00:00')
      },
      {
        id: 6,
        icon: 'how_to_reg',
        iconColor: '#2563eb',
        description: 'Enrollment #547 submitted — Individual Rental, pending review',
        timestamp: new Date('2026-03-30T13:35:00')
      }
    ]))
    );
  }
}
